// src/pages/AboutPage.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import "../Component/Home/theme.css";
import { MessageSquare, CheckCircle, Award,  Calendar,  ThumbsUp } from 'lucide-react';
import Shaker from "../Assesst/CandyWhite.png";
import matte from "../Assesst/smokeP.jpeg";
import gloss from "../Assesst/CeramicgR.png";
import Acrilic from "../Assesst/acrilic.jpg";

const AboutPage = () => {
  const navigate = useNavigate();
  const [expandedService, setExpandedService] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const businessInfo = {
    name: "Laminance Cabinetry",
    fullName: "Laminance Cabinetry - Custom Kitchen Cabinet Specialists",
    founded: "2024",
    address: "418 Rt 23, Franklin, NJ, 07416",
    phone: "(862) 450-6069",
    email: "Contactus@laminance.com",
    area: "Entire New Jersey",
    description: "Premium custom kitchen cabinet craftsmanship and complete kitchen transformations in New Jersey. Specializing in acrylic cabinets, shaker cabinets, matte finishes, and luxury kitchen designs.",
    foundingDate: "2023-10-01",
    yearsOfExperience: "15+",
    Coloroption: "500+",
    satisfactionRate: "99%"
  };

  const transformationPackage = {
    startingPrice: "$25,000",
    title: "Complete Kitchen Transformation",
    description: "Everything you need for a complete kitchen renovation, handled by our expert team from start to finish.",
    includes: [
      { title: "Premium Cabinets", icon: "🗄️", details: "Custom-built cabinets with your choice of acrylic, matte, gloss, or shaker styles. Over 290+ color options available." },
      { title: "Luxury Countertops", icon: "🪨", details: "Quartz, granite, or marble countertop installation with professional sealing and lifetime warranty." },
      { title: "Designer Backsplash", icon: "🧱", details: "Professional backsplash design with premium tile selection and installation. Custom patterns available." },
      { title: "Professional Painting", icon: "🎨", details: "Complete kitchen painting with premium, low-VOC paints and protective coatings. Multiple finish options." },
      { title: "High-End Sink & Plumbing", icon: "🚰", details: "Undermount or farmhouse sink with professional plumbing and garbage disposal. Premium faucet included." },
      { title: "Premium Hardware", icon: "🔩", details: "Hand-selected knobs, pulls, soft-close hinges, and all finishing hardware from top brands." },
      { title: "Electrical Updates", icon: "💡", details: "LED lighting fixtures, USB outlets, under-cabinet lighting, and all necessary electrical work." },
      { title: "Complete Installation", icon: "👷", details: "Full labor from demolition to final inspection and cleanup. Licensed and insured professionals." }
    ]
  };

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  // Enhanced Schema Data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": businessInfo.name,
    "alternateName": "Laminance Cabinetry",
    "description": businessInfo.description,
    "url": "https://laminance.com",
    "logo": "https://laminance.com/assets/Images/logoimg.png",
    "image": "https://laminance.com/assets/Images/logoimg.png",
    "telephone": businessInfo.phone,
    "email": businessInfo.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.1220",
      "longitude": "-74.5804"
    },
    "priceRange": "$$$",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "16:00"
      }
    ],
    "founder": "Laminance Team",
    "foundingDate": businessInfo.foundingDate,
    "areaServed": {
      "@type": "State",
      "name": "New Jersey"
    },
    "sameAs": [
      "https://www.facebook.com/laminancecabinetry",
      "https://www.instagram.com/laminancecabinetry"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "bestRating": "5",
      "ratingCount": "200"
    }
  };

 const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",

  "serviceType": "Kitchen Renovation and Custom Cabinet Installation",

  "name": transformationPackage.title,

  "description": transformationPackage.description,

  "provider": {
    "@type": "Organization",
    "name": businessInfo.name,
    "url": "https://laminance.com",
    "telephone": "+18624506069",
    "logo": "https://laminance.com/assets/Images/logo.png"
  },

  "areaServed": {
    "@type": "State",
    "name": "New Jersey"
  },

  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Kitchen Services",

    "itemListElement": transformationPackage.includes.map(
      (item, index) => ({
        "@type": "Offer",

        "position": index + 1,

        "availability": "https://schema.org/InStock",

        "url": "https://laminance.com/contact",

        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "USD"
        },

        "itemOffered": {
          "@type": "Service",
          "name": item.title,
          "description": item.details
        }
      })
    )
  }
};

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does a complete kitchen renovation take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A complete kitchen renovation typically takes 2-4 weeks depending on the scope of work. Our all-inclusive package includes cabinets, countertops, backsplash, electrical, and plumbing - all coordinated to minimize disruption."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do you serve for kitchen cabinet installation?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Laminance Cabinetry proudly serves the entire New Jersey and Tri-State area, including Franklin, Bergen County, Essex County, Morris County, Passaic County, and surrounding communities."
        }
      },
      {
        "@type": "Question",
        "name": "What makes acrylic cabinets better than standard cabinets?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our acrylic cabinets feature a high-gloss, scratch-resistant finish that is easier to clean, more durable, and more resistant to moisture than standard laminated cabinets. They provide a modern, luxurious look that maintains its beauty for years."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide free consultation and quotes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we offer free in-home consultations and design services. Our experts will visit your home, take measurements, discuss your vision, and provide a detailed quote with no obligation."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": businessInfo.name,
    "description": businessInfo.description,
    "url": "https://laminance.com",
    "telephone": businessInfo.phone,
    "priceRange": "$$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "review": [
      {
        "@type": "Review",
        "author": "Kousik Bera",
        "datePublished": "2025-01-15",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "Laminance kitchen did an exceptional job in making our dreams of a unique kind of kitchen in USA."
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>About Laminance Cabinetry | Custom Kitchen Cabinets New Jersey | Premium Kitchen Transformations</title>
        <meta name="title" content="About Laminance Cabinetry | Custom Kitchen Cabinets New Jersey | Premium Kitchen Transformations Starting at $25,000" />
        <meta name="description" content={`${businessInfo.name} - New Jersey's premier custom kitchen cabinet specialists. Complete kitchen transformations starting at $25,000. Over ${businessInfo.projectsCompleted} projects completed with ${businessInfo.satisfactionRate} satisfaction rate. Free consultation!`} />
        <meta name="keywords" content="Laminance Cabinetry, custom kitchen cabinets New Jersey, kitchen renovation NJ, shaker cabinets, acrylic cabinets, complete kitchen transformation, Franklin NJ kitchen cabinets, kitchen remodeling, cabinet installation NJ, custom cabinetry" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="Laminance Cabinetry" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://laminance.com/about" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://laminance.com/about" />
        <meta property="og:title" content="About Laminance Cabinetry | New Jersey's Premium Custom Kitchen Cabinet Specialists" />
        <meta property="og:description" content={`Complete kitchen transformations starting at ${transformationPackage.startingPrice}. ${businessInfo.Coloroption}+ Color Option. Free consultation in New Jersey.`} />
        <meta property="og:image" content="https://laminance.com/assets/Images/logoimg.png" />
        <meta property="og:image:alt" content="Laminance Cabinetry - Premium custom kitchen cabinets New Jersey" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Laminance Cabinetry" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://laminance.com/about" />
        <meta name="twitter:title" content="About Laminance Cabinetry | Custom Kitchen Cabinets New Jersey" />
        <meta name="twitter:description" content={`Premium kitchen transformations starting at ${transformationPackage.startingPrice}. Free consultation.`} />
        <meta name="twitter:image" content="https://laminance.com/assets/Images/logoimg.png" />
        <meta name="twitter:site" content="@laminance" />
        
        {/* Geo Meta Tags for Local SEO */}
        <meta name="geo.region" content="US-NJ" />
        <meta name="geo.placename" content="Franklin" />
        <meta name="geo.position" content="41.1220;-74.5804" />
        <meta name="ICBM" content="41.1220, -74.5804" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="revisit-after" content="7 days" />
        <meta name="language" content="English" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="General" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <div className="min-h-screen inter-secondary" style={{ background: "var(--gradient-bg)" }}>
        {/* Hidden SEO Content for Crawlers */}
        <div className="sr-only" aria-hidden="true">
          <h1>Laminance Cabinetry - Custom Kitchen Cabinet Specialists in New Jersey</h1>
          <p>Laminance Cabinetry is the leading provider of custom kitchen cabinets and complete kitchen transformations in New Jersey. With over {businessInfo.yearsOfExperience} years of experience and {businessInfo.projectsCompleted}+ successful projects, we are the trusted choice for homeowners looking to transform their kitchens.</p>
          <p>Our services include custom kitchen cabinets, acrylic cabinets, shaker cabinets, matte finish cabinets, luxury countertops, designer backsplash, professional painting, sink and faucet installation, cabinet hardware, electrical updates, and complete installation.</p>
          <p>We serve the entire state of New Jersey including Franklin, Bergen County, Essex County, Morris County, Passaic County, and surrounding areas. Contact us today for a free consultation at {businessInfo.phone}.</p>
        </div>

        {/* Hidden SEO Image */}
        <img src="https://laminance.com/assets/Images/logoimg.png" alt="Laminance Cabinetry - Premium custom kitchen cabinets New Jersey" className="hidden" loading="eager" fetchPriority="high" width="1200" height="630" />

        {/* Hero Section */}
        <section className="pt-12 md:pt-16 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 mx-auto"
                style={{ background: "var(--bg-primary-10)", border: "1px solid var(--border-primary-20)" }}
              >
                <MessageSquare size={14} style={{ color: "var(--color-primary-400)" }} />
                <span className="text-sm font-medium" style={{ color: "var(--color-primary-400)" }}>Trusted Since 2024</span>
              </div>
              
              <h1 className="bell-mt-primary text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
                style={{ color: "var(--color-primary-500)" }}
              >
                About <span style={{ background: "var(--gradient-primary)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Laminance Cabinetry</span>
              </h1>
              
              <p className="inter-secondary text-base sm:text-lg md:text-xl max-w-3xl mx-auto"
                style={{ color: "var(--color-gray-600)" }}
              >
                New Jersey's premier custom kitchen cabinet specialists, delivering premium quality and exceptional service.
              </p>
              
              <div className="flex justify-center mt-6">
                <div className="w-24 h-1 rounded-full" style={{ background: "var(--gradient-primary)" }}></div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: businessInfo.yearsOfExperience, label: "Years Experience", icon: Award },
                { value: businessInfo.Coloroption, label: "Color Option", icon: CheckCircle },
                { value: businessInfo.satisfactionRate, label: "Satisfaction Rate", icon: ThumbsUp },
                { value: "Free", label: "Consultation", icon: Calendar }
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center p-4 rounded-xl bg-white shadow-md"
                    style={{ border: "1px solid var(--border-light)" }}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--color-primary-500)" }} />
                    <div className="text-xl sm:text-2xl font-bold" style={{ color: "var(--color-primary-500)" }}>{stat.value}</div>
                    <div className="text-xs sm:text-sm" style={{ color: "var(--color-gray-600)" }}>{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Premium Package Showcase */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="bell-mt-primary text-4xl sm:text-4xl md:text-5xl font-bold mb-3"
                style={{ color: "var(--color-primary-500)" }}
              >
                {transformationPackage.title}
              </h2>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-4">
                <span className="bell-mt-primary text-3xl sm:text-4xl md:text-5xl font-bold"
                  style={{ color: "var(--color-primary-500)" }}
                >
                  {transformationPackage.startingPrice}
                </span>
                <span className="inter-secondary text-base sm:text-lg"
                  style={{ color: "var(--color-gray-600)" }}
                >
                  Starting Price - Complete Kitchen Transformation
                </span>
              </div>
              
              <p className="inter-secondary text-base sm:text-lg max-w-3xl mx-auto"
                style={{ color: "var(--color-gray-600)" }}
              >
                {transformationPackage.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
              {transformationPackage.includes.map((item, index) => (
                <div key={index} className="group relative rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
                  style={{ backgroundColor: "var(--color-white)", boxShadow: "var(--shadow-medium)" }}
                  onMouseEnter={() => !isMobile && setExpandedService(index)}
                  onMouseLeave={() => !isMobile && setExpandedService(null)}
                  onClick={() => isMobile && setExpandedService(expandedService === index ? null : index)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl sm:text-3xl" aria-hidden="true">{item.icon}</div>
                    <div className="flex-1">
                      <h3 className="bell-mt-primary text-base sm:text-lg font-bold mb-1" style={{ color: "var(--color-primary-600)" }}>
                        {item.title}
                      </h3>
                      <p className="inter-secondary text-xs sm:text-sm leading-relaxed" style={{ color: "var(--color-gray-600)" }}>
                        {expandedService === index || isMobile ? item.details : `${item.details.substring(0, 60)}...`}
                      </p>
                    </div>
                  </div>
                  {(expandedService === index || isMobile) && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-xl" style={{ background: "var(--gradient-primary)" }}></div>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-xl p-6 md:p-10 text-center relative overflow-hidden"
              style={{ background: "var(--gradient-primary)", boxShadow: "var(--shadow-large)" }}
            >
              <div className="relative z-10">
                <h3 className="bell-mt-primary text-xl sm:text-2xl md:text-3xl font-bold mb-4" style={{ color: "var(--color-white)" }}>
                  Ready for Your Dream Kitchen?
                </h3>
                <p className="inter-secondary text-sm sm:text-base md:text-lg mb-6 max-w-2xl mx-auto" style={{ color: "rgba(255, 255, 255, 0.95)" }}>
                  Join over {businessInfo.projectsCompleted} satisfied homeowners who transformed their kitchens with Laminance Cabinetry.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button onClick={() => handleNavigation('/contact#contact-form')}
                    className="inter-secondary px-6 sm:px-8 py-3 font-bold rounded-xl bg-white hover:bg-gray-100 transition-all duration-300"
                    style={{ color: "var(--color-primary-600)" }}
                  >
                    Book Free Consultation
                  </button>
                  <button onClick={() => navigate('/', { state: { scrollToCollections: true } })}
                    className="inter-secondary px-6 sm:px-8 py-3 font-bold rounded-xl transition-all duration-300"
                    style={{ border: "2px solid var(--color-white)", color: "var(--color-white)", backgroundColor: "transparent" }}
                  >
                    View Our Work
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section - Enhanced SEO Content */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="bell-mt-primary text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
                style={{ color: "var(--color-primary-500)" }}
              >
                Why New Jersey Homeowners Choose Laminance Cabinetry
              </h2>
              <p className="inter-secondary text-base sm:text-lg max-w-3xl mx-auto" style={{ color: "var(--color-gray-600)" }}>
                Discover what makes us the trusted choice for custom kitchen cabinets and complete kitchen transformations across New Jersey.
              </p>
              <div className="flex justify-center mt-6">
                <div className="w-24 h-1 rounded-full" style={{ background: "var(--gradient-primary)" }}></div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="rounded-xl p-6 md:p-8 shadow-xl" style={{ background: "var(--color-primary-500)" }}>
                <h3 className="bell-mt-primary text-2xl sm:text-3xl font-bold mb-4" style={{ color: "var(--color-white)" }}>
                  The Laminance Difference
                </h3>
                <div className="space-y-4">
                  <p className="inter-secondary text-base" style={{ color: "rgba(255,255,255,0.95)" }}>
                    Since our founding in {businessInfo.founded}, we've been dedicated to transforming kitchens across {businessInfo.area} with uncompromising quality and attention to detail.
                  </p>
                  <p className="inter-secondary text-base" style={{ color: "rgba(255,255,255,0.95)" }}>
                    Every cabinet we create is more than just storage—it's a functional work of art, meticulously crafted to enhance your daily life while adding lasting value to your home.
                  </p>
                  <p className="inter-secondary text-base" style={{ color: "rgba(255,255,255,0.95)" }}>
                    Our commitment to excellence has earned us a 5-star rating and the trust of hundreds of homeowners throughout New Jersey.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { stat: "500+", label: "Color Options", icon: "🎨" },
                  { stat: "3D", label: "Free Design", icon: "🖥️" },
                  { stat: "24hr", label: "Quote Response", icon: "⚡" },
                  { stat: "100%", label: "Handcrafted", icon: "✋" }
                ].map((item, index) => (
                  <div key={index} className="text-center p-4 rounded-xl border bg-white shadow-md"
                    style={{ borderColor: "var(--border-light)" }}
                  >
                    <div className="text-2xl sm:text-3xl mb-2" style={{ color: "var(--color-primary-500)" }}>{item.icon}</div>
                    <div className="bell-mt-primary text-xl sm:text-2xl font-bold" style={{ color: "var(--color-primary-500)" }}>{item.stat}</div>
                    <div className="inter-secondary text-sm" style={{ color: "var(--color-gray-600)" }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Premium Cabinet Collection - Enhanced */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 mx-auto"
                style={{ background: "var(--bg-primary-10)", border: "1px solid var(--border-primary-20)" }}
              >
                <MessageSquare size={14} style={{ color: "var(--color-primary-400)" }} />
                <span className="text-sm font-medium" style={{ color: "var(--color-primary-400)" }}>Our Cabinets</span>
              </div>
              <h2 className="bell-mt-primary text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
                style={{ color: "var(--color-primary-500)" }}
              >
                Premium Cabinet Collection
              </h2>
              <p className="inter-secondary text-base sm:text-lg max-w-3xl mx-auto" style={{ color: "var(--color-gray-600)" }}>
                From classic shaker to modern Lamigloss, discover our extensive range of premium kitchen cabinets in over 290+ colors.
              </p>
              <div className="flex justify-center mt-6">
                <div className="w-24 h-1 rounded-full" style={{ background: "var(--gradient-primary)" }}></div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Shaker Cabinets", description: "Timeless classic with clean, simple lines - the most requested cabinet style in New Jersey", features: ["Raised panel design", "Versatile styling", "Multiple finishes"], popular: true, icon: Shaker, alt: "Premium Shaker kitchen cabinets by Laminance Cabinetry" },
                { title: "Acrylic Cabinets", description: "High-reflective luxury finish that brings modern elegance to any kitchen", features: ["Mirror-like surface", "Easy maintenance", "Modern aesthetic"], premium: true, icon: Acrilic, alt: "High-gloss Acrylic kitchen cabinets - Laminance Cabinetry" },
                { title: "Super Matte Cabinets", description: "Luxury textured surface with anti-fingerprint technology", features: ["Fingerprint resistant", "Sophisticated look", "Warm appearance"], icon: matte, alt: "Super Matte finish kitchen cabinets - Laminance Cabinetry" },
                { title: "Lamigloss Cabinets", description: "Contemporary high-gloss finish for a sleek, modern look", features: ["Sleek appearance", "Scratch-resistant", "Color variety"], icon: gloss, alt: "Lamigloss high-gloss kitchen cabinets - Laminance Cabinetry" }
              ].map((cabinet, index) => (
                <div key={index} className="group relative p-6 rounded-xl border shadow-lg hover:shadow-2xl transition-all duration-300"
                  style={{ backgroundColor: "var(--color-white)", borderColor: "var(--border-light)" }}
                >
                  {(cabinet.popular || cabinet.premium) && (
                    <div className="absolute -top-2 left-4 px-3 py-0.5 text-white text-xs font-bold rounded-full"
                      style={{ background: "var(--gradient-primary)" }}
                    >
                      {cabinet.popular ? "Most Popular" : "Premium Choice"}
                    </div>
                  )}
                  
                  <div className="w-16 h-16 rounded-xl overflow-hidden mb-4 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: "var(--color-primary-50)" }}
                  >
                    <img src={cabinet.icon} alt={cabinet.alt} className="w-full h-full object-cover" loading="lazy" width="80" height="80" decoding="async" />
                  </div>

                  <h3 className="bell-mt-primary text-lg font-bold mb-2" style={{ color: "var(--color-primary-500)" }}>
                    {cabinet.title}
                  </h3>
                  <p className="inter-secondary text-sm mb-4" style={{ color: "var(--color-gray-600)" }}>
                    {cabinet.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {cabinet.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <div className="w-1.5 h-1.5 rounded-full mt-1.5 mr-2 flex-shrink-0"
                          style={{ backgroundColor: "var(--color-primary-500)" }}
                        ></div>
                        <span className="inter-secondary text-xs" style={{ color: "var(--color-gray-600)" }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Rich Content Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="bell-mt-primary text-2xl sm:text-3xl font-bold mb-4" style={{ color: "var(--color-primary-500)" }}>
                Custom Kitchen Cabinets & Complete Kitchen Renovations in New Jersey
              </h2>
              <div className="flex justify-center mt-4">
                <div className="w-24 h-1 rounded-full" style={{ background: "var(--gradient-primary)" }}></div>
              </div>
            </div>
            
            <div className="space-y-6 text-center">
              <p className="inter-secondary text-base leading-relaxed" style={{ color: "var(--color-gray-600)" }}>
                At Laminance Cabinetry, we specialize in creating stunning, functional kitchens that stand the test of time. 
                Serving homeowners throughout <strong>New Jersey</strong>, we bring together premium materials, expert craftsmanship, 
                and innovative design to deliver kitchen transformations that exceed expectations.
              </p>
              
              <p className="inter-secondary text-base leading-relaxed" style={{ color: "var(--color-gray-600)" }}>
                Our <strong>all-inclusive kitchen transformation packages</strong> start at <strong>$25,000</strong> and cover everything 
                from custom cabinets and luxury countertops to professional installation and project management. 
                With over <strong>500+ color options</strong> and multiple cabinet styles including shaker, acrylic, matte, and gloss finishes, 
                we have the perfect solution for every taste and budget.
              </p>
              
              <p className="inter-secondary text-base leading-relaxed" style={{ color: "var(--color-gray-600)" }}>
                Whether you're looking for a <strong>modern kitchen makeover</strong>, a <strong>traditional cabinet installation</strong>, 
                or a <strong>complete kitchen renovation</strong>, our team of experts is ready to bring your vision to life. 
                Contact us today for a <strong>free consultation</strong> and discover why we're the trusted choice for custom kitchen cabinets in New Jersey.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {["Custom Kitchen Cabinets", "Acrylic Cabinets", "Shaker Cabinets", "Kitchen Renovation", "Cabinet Installation", "Free Consultation", "3D Kitchen Design", "Premium Countertops"].map((keyword, idx) => (
                <span key={idx} className="inter-secondary text-xs px-3 py-1 rounded-full"
                  style={{ backgroundColor: "var(--bg-primary-10)", color: "var(--color-primary-600)" }}
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section for Rich Snippets */}
        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="bell-mt-primary text-2xl sm:text-3xl font-bold mb-4" style={{ color: "var(--color-primary-500)" }}>
                Frequently Asked Questions About Kitchen Renovation
              </h2>
              <div className="flex justify-center mt-4">
                <div className="w-24 h-1 rounded-full" style={{ background: "var(--gradient-primary)" }}></div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { q: "How long does a complete kitchen renovation take?", a: "A complete kitchen renovation typically takes 2-4 weeks depending on the scope of work. Our all-inclusive package coordinates everything to minimize disruption to your daily life." },
                { q: "What is included in your custom kitchen cabinet installation?", a: "Our custom kitchen cabinet installation includes design consultation, precise measurements, custom cabinet manufacturing, professional installation, hardware installation, and final adjustments." },
                { q: "Do you provide countertops with the all-inclusive package?", a: "Yes, our all-inclusive kitchen transformation includes premium countertops. You can choose from quartz, granite, marble, or solid surface options." },
                { q: "What areas do you serve for kitchen cabinet installation?", a: "Laminance Cabinetry proudly serves the entire New Jersey and Tri-State area, including Franklin, Bergen County, Essex County, Morris County, Passaic County, and surrounding communities." }
              ].map((faq, idx) => (
                <div key={idx} className="p-4 rounded-xl border bg-white"
                  style={{ borderColor: "var(--border-light)" }}
                >
                  <h3 className="bell-mt-primary font-semibold mb-2" style={{ color: "var(--color-primary-500)" }}>{faq.q}</h3>
                  <p className="inter-secondary text-sm" style={{ color: "var(--color-gray-600)" }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-xl p-6 md:p-10 shadow-xl" style={{ background: "var(--color-primary-500)" }}>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h2 className="bell-mt-primary text-2xl sm:text-3xl font-bold mb-4" style={{ color: "var(--color-white)" }}>
                    Get In Touch
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="bell-mt-primary text-xl font-bold mb-1" style={{ color: "var(--color-white)" }}>{businessInfo.name}</h3>
                      <p className="inter-secondary text-sm" style={{ color: "#ece6e6" }}>{businessInfo.address}</p>
                    </div>
                    <div>
                      <p className="inter-secondary text-sm"><strong style={{ color: "var(--color-white)" }}>Phone:</strong> <a href={`tel:${businessInfo.phone.replace(/\D/g, '')}`} style={{ color: "#ece6e6" }}>{businessInfo.phone}</a></p>
                      <p className="inter-secondary text-sm"><strong style={{ color: "var(--color-white)" }}>Email:</strong> <a href={`mailto:${businessInfo.email}`} style={{ color: "#ece6e6" }}>{businessInfo.email}</a></p>
                    </div>
                    <div>
                      <h4 className="bell-mt-primary font-semibold mb-1" style={{ color: "var(--color-white)" }}>Service Area</h4>
                      <p className="inter-secondary text-sm" style={{ color: "#ece6e6" }}>{businessInfo.area}</p>
                    </div>
                    <div>
                      <h4 className="bell-mt-primary font-semibold mb-1" style={{ color: "var(--color-white)" }}>Business Hours</h4>
                      <p className="inter-secondary text-sm" style={{ color: "#ece6e6" }}>Mon-Fri: 9AM-6PM | Sat: 10AM-4PM | Sun: By Appointment</p>
                    </div>
                  </div>
                </div>
                
                <div className="rounded-xl p-6 bg-white shadow-lg">
                  <h3 className="bell-mt-primary text-xl font-bold mb-4" style={{ color: "var(--color-primary-500)" }}>Ready to Start Your Project?</h3>
                  <div className="space-y-3">
                    <a href={`tel:${businessInfo.phone.replace(/\D/g, '')}`}
                      className="inter-secondary flex items-center justify-center w-full px-6 py-3 font-bold rounded-lg transition-all text-center"
                      style={{ background: "var(--gradient-primary)", color: "var(--color-white)" }}
                    >
                      📞 Call Now: {businessInfo.phone}
                    </a>
                    <a href={`mailto:${businessInfo.email}`}
                      className="inter-secondary flex items-center justify-center w-full px-6 py-3 font-bold rounded-lg transition-all text-center"
                      style={{ border: "2px solid var(--color-primary-500)", color: "var(--color-primary-600)" }}
                    >
                      ✉️ Email Us
                    </a>
                    <button onClick={() => handleNavigation('/contact#contact-form')}
                      className="inter-secondary w-full px-6 py-3 font-bold rounded-lg transition-all"
                      style={{ backgroundColor: "var(--color-gray-900)", color: "var(--color-white)" }}
                    >
                      📝 Contact Form
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer SEO Keywords */}
        <div className="text-center py-6 px-4">
          <p className="inter-secondary text-xs opacity-60" style={{ color: "var(--color-gray-500)" }}>
            <strong>Custom Kitchen Cabinets New Jersey | Acrylic Cabinets | Shaker Cabinets | Matte Finish Cabinets | Lamigloss Cabinets | Kitchen Renovation | Cabinet Installation | Modern Kitchen Design | Complete Kitchen Transformation | Free Consultation | 3D Kitchen Design | Premium Countertops | Backsplash Installation | Kitchen Painting | Cabinet Hardware | Electrical Updates | Licensed & Insured | 5-Star Rated | New Jersey Kitchen Experts</strong>
          </p>
        </div>
      </div>
    </>
  );
};

export default AboutPage;